<template>
    <div class="w-100 align-self-start">
        <v-card flat color="transparent">
            <v-card-title class="d-block">
                <v-btn class="mb-3" small text color="secondary" :to="{ name: 'app-sessions', params: { aid: $route.params.aid } }">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Application sessions
                </v-btn>
                <div>Session logs</div>
            </v-card-title>
            <v-card-subtitle class="d-flex align-center">
                <img width="18" height="18" :src="appIcon(selectedApp)" class="mr-2" />
                {{ selectedApp.long_id }}
                <v-divider vertical class="mx-3 my-1"></v-divider>
                <v-chip color="grey lighten-3" label>
                    {{ this.$route.params.sessId }}
                </v-chip>
            </v-card-subtitle>
            <v-card-text class="d-flex">
                <v-alert type="info" text dense class="mb-0 mr-2">We only keep logs for 7 days.</v-alert>
            </v-card-text>
            <v-card-text>
                <v-container fluid class="pa-0">
                    <v-row>
                        <v-col md="3" xl="2">
                            <v-card>
                                <v-card-text>
                                    <v-skeleton-loader type="article" v-if="loadingSession"></v-skeleton-loader>
                                    <v-list nav class="pa-0" v-else>
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title>Containers</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-list-item-group v-model="selectedContainer" mandatory color="primary">
                                            <v-list-item v-for="(container, i) in containers" :key="i" @click="fetchLogs(container)">
                                                <v-list-item-content class="text-uppercase">
                                                    {{ container }}
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list-item-group>
                                    </v-list>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col md="9" xl="10">
                            <div class="d-flex align-center">
                                <v-sheet class="rounded py-2 px-3" color="white mr-3">
                                    <v-switch v-model="showLogTime" label="Show timestamp" inset dense hide-details class="my-0 pa-0"></v-switch>
                                </v-sheet>
                                <v-select
                                    :items="numLines"
                                    solo
                                    style="max-width: 170px"
                                    dense
                                    hide-details
                                    label="Number of lines"
                                    v-model="selectedNumLines"
                                    @change="fetchSelectedContainerLogs"
                                    class="mr-3"></v-select>
                                <v-sheet class="rounded py-1 pl-3 d-flex align-center" color="white mr-3">
                                    <v-switch v-model="useAfterTimestamp" inset dense hide-details class="my-0 pa-0" @click.stop></v-switch>
                                    <v-menu offset-y :close-on-content-click="false" v-model="afterTimestamopModal">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn v-bind="attrs" small v-on="on" class="mr-3" height="30" :disabled="!useAfterTimestamp">
                                                After timestamp
                                                <v-icon right>mdi-chevron-down</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-card>
                                            <v-card-title>UTC time</v-card-title>
                                            <v-card-text>
                                                <nv-date-picker v-model="logDate" :allowed-dates="allowedDates"></nv-date-picker>
                                                <time-picker v-model="logTime" :icon="false" use-seconds format="24hr" style="max-width: 290px"></time-picker>
                                            </v-card-text>
                                            <v-card-actions>
                                                <v-spacer></v-spacer>
                                                <v-btn color="primary" @click="setLogTime()">Ok</v-btn>
                                            </v-card-actions>
                                        </v-card>
                                    </v-menu>
                                </v-sheet>

                                <v-text-field
                                    prepend-inner-icon="mdi-magnify "
                                    autofocus
                                    hide-details
                                    solo
                                    flat
                                    dense
                                    placeholder="Search logs..."
                                    v-model="search"
                                    clearable
                                    class="mr-2"></v-text-field>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-btn icon v-on="on" :loading="loadingLogs" @click="fetchLogs(containers[selectedContainer])">
                                            <v-icon>refresh</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Refresh logs</span>
                                </v-tooltip>
                            </div>
                            <v-alert v-if="error" type="error" text class="mt-3">
                                {{ error }}
                            </v-alert>
                            <div v-else class="copy-wrapper">
                                <div class="copy">
                                    <copy-to-clipboard
                                        v-if="filteredLogs?.length"
                                        :textToCopy="logToCopy"
                                        buttonColor="white"
                                        buttonName="copy to clipboard"
                                        :iconButton="true"></copy-to-clipboard>
                                </div>
                                <v-card class="overflow-auto mt-3" dark ref="logCard" :max-height="maxHeight" :loading="loadingLogs">
                                    <v-card-text v-ctrl-select>
                                        <div v-for="(log, i) in filteredLogs" :key="`log-${i}`">
                                            <code class="white--text text-no-wrap">
                                                <span class="light-blue--text" v-if="showLogTime">[{{ log.ts | dateTimeToHuman }}]</span>
                                                <span class="light-blue--text" v-else>{{ i + 1 }}.</span>
                                                <pre class="d-inline ml-3" v-html="log.msg"></pre>
                                            </code>
                                        </div>
                                        <div ref="bottom"></div>
                                        <v-alert v-if="!loadingSession && !loadingLogs && !filteredLogs?.length" type="info" text class="mt-3">
                                            No logs found
                                        </v-alert>
                                    </v-card-text>
                                </v-card>
                            </div>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import NvDatePicker from '@/components/NvDatePicker.vue'
import { mapGetters, mapState } from 'vuex'
import { scaling } from '@/mixins/scaling'
import { enumsData } from '@/mixins/enums'
import { appTypeAndImageLink } from '@/mixins/appTypeAndImage'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import TimePicker from '@/components/TimePicker.vue'
import { dateTimeConcat } from '@/utils'

const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')

export default {
    name: 'SessionLogs',
    mixins: [scaling, enumsData, appTypeAndImageLink],
    components: { NvDatePicker, TimePicker, CopyToClipboard },
    data() {
        return {
            loadingSession: false,
            loadingLogs: false,
            search: '',
            logs: [],
            selectedContainer: 0,
            error: '',
            selectedApp: null,
            maxHeight: '',
            showLogTime: true,
            numLines: [100, 1000, 10000],
            selectedNumLines: 100,
            useAfterTimestamp: false,
            afterTimestamopModal: false,
            logDate: DateTime.now().toISODate(),
            logTime: DateTime.now().toFormat('T'),
            session: null
        }
    },
    created() {
        this.fetchSession()
    },
    computed: {
        ...mapGetters('spaceStore', ['currentSpaceType']),
        ...mapState('snapshotStore', ['applications', 'applicationsFetching']),
        filteredLogs() {
            if (!this.search) {
                return this.logs
            }
            return this.logs.filter(log => log.msg.toLowerCase().includes(this.search.toLowerCase()))
        },
        containers() {
            if (!this.session) return null

            const containers = this.session.logging_containers
            const mainAppIndex = containers.indexOf('main-app')
            containers.splice(mainAppIndex, 1)
            containers.unshift('main-app')
            return containers
        },
        allowedDates() {
            const today = DateTime.now()
            const sevenDaysAgo = today.minus({ days: 7 })
            return date => {
                const d = DateTime.fromISO(date)
                return d >= sevenDaysAgo && d <= today
            }
        },
        logToCopy() {
            return this.filteredLogs
                .map((log, i) => {
                    if (this.showLogTime) {
                        return `[${this.$options.filters.dateTimeToHuman(log.ts)}] ${log.msg}`
                    } else {
                        return `${i + 1}. ${log.msg}`
                    }
                })
                .join('\n')
        }
    },
    methods: {
        async fetchSession() {
            this.error = ''
            this.loadingSession = true
            try {
                const { data } = await this.$axios.get(`/applications/${this.$route.params.aid}/sessions?session_id=${this.$route.params.sessId}`)
                this.session = data[0]
            } catch (error) {
                this.error = error.message
            }
            this.loadingSession = false
        },
        async fetchLogs(container) {
            this.error = ''
            this.loadingLogs = true
            try {
                let query = `?max_lines=${this.selectedNumLines}`
                if (this.useAfterTimestamp) {
                    const dateTime = dateTimeConcat(this.logDate, this.logTime)
                    query += `&from_start=${dateTime}`
                }

                const { data } = await this.$axios.get(`/sessions/${this.$route.params.sessId}/container/${container}/logs${query}`)
                this.logs = orderBy(data, 'ts')
            } catch (error) {
                this.error = error.message
            }
            this.loadingLogs = false
        },
        getMaxHeight() {
            this.$nextTick(() => {
                const card = this.$refs.logCard.$el
                const top = card.getBoundingClientRect().top
                const clientHeight = window.innerHeight
                // 128 comes from main padding bottom
                this.maxHeight = clientHeight - top - 128
            })
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const el = this.$refs.bottom
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth' })
                }
            })
        },
        fetchSelectedContainerLogs() {
            this.fetchLogs(this.containers[this.selectedContainer])
        },
        setLogTime() {
            this.fetchSelectedContainerLogs()
            this.afterTimestamopModal = false
        }
    },
    watch: {
        applicationsFetching: {
            handler(fetching) {
                if (!fetching) {
                    this.selectedApp = this.applications.find(app => app.aid === Number(this.$route.params.aid))
                }
            },
            immediate: true
        },
        containers: {
            handler(container) {
                if (container.length) {
                    this.fetchLogs(container[0])
                }
            },
            immediate: true
        },
        loadingLogs: {
            handler(loading) {
                if (!loading) {
                    this.getMaxHeight()
                    setTimeout(() => {
                        this.scrollToBottom()
                    }, 0)
                }
            },
            immediate: true
        }
    }
}
</script>

<style scoped lang="scss">
.copy {
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1;
    margin: 15px 30px;
}

.copy-wrapper {
    position: relative;
}
</style>
